import { IndustryType, SectionType } from '../types';
import { getIndustryConfig, isFeatureEnabledForIndustry, IndustryModuleConfig } from '../config/industries';
import { COMPONENT_REGISTRY, SectionDescriptor } from '../components/editor/registry';

export type ModuleKey =
  | 'pages'
  | 'services'
  | 'about'
  | 'team'
  | 'references'
  | 'gallery'
  | 'faq'
  | 'contact'
  | 'communications'
  | 'blog'
  | 'yachts'
  | 'yachtDetails'
  | 'routes'
  | 'marinas'
  | 'reservation'
  | 'calendar'
  | 'seo'
  | 'domains';

export interface EditorCategoryTab {
  id: string;
  name: string;
  description: string;
  sectionTypes: SectionType[];
}

export class CapabilityService {
  /**
   * Check if a vertical module is accessible for a given industry
   */
  public canAccessModule(industry: IndustryType, moduleKey: ModuleKey): boolean {
    return isFeatureEnabledForIndustry(industry, moduleKey as keyof IndustryModuleConfig['enabledModules']);
  }

  /**
   * Strict separation check: whether a section type is allowed for the industry
   */
  public canUseSection(industry: IndustryType, sectionType: SectionType): boolean {
    const config = getIndustryConfig(industry);
    return config.allowedSections.includes(sectionType);
  }

  /**
   * Return allowed section descriptors for the industry from the registry
   */
  public getAvailableSections(industry: IndustryType): SectionDescriptor[] {
    return Object.values(COMPONENT_REGISTRY).filter((descriptor) =>
      descriptor.allowedIndustries.includes(industry)
    );
  }

  /**
   * Return structured editor categories matching user specification:
   * For Yacht: İçerik, Yatlar, Rezervasyon, Tasarım, Düzen, SEO, Gelişmiş
   * For Corporate: Giriş & Başlık, Kurumsal & İçerik, Fiyatlandırma, Medya & Güven, İletişim & Alt Bilgi
   */
  public getEditorCategories(industry: IndustryType): EditorCategoryTab[] {
    if (industry === 'YACHT_RENTAL') {
      return [
        {
          id: 'content',
          name: 'İçerik & Tanıtım',
          description: 'Manşet (Hero), Hakkımızda ve Sıkça Sorulan Sorular',
          sectionTypes: ['HEADER', 'HERO', 'ABOUT', 'FAQ'],
        },
        {
          id: 'yachts',
          name: 'Yatlar & Filo',
          description: 'Kiralık Filo Vitrini, Arama/Filtre, Mavi Tur Rotaları ve Marinalar',
          sectionTypes: ['YACHT_FLEET', 'YACHT_FILTER', 'YACHT_ROUTES', 'YACHT_MARINAS'],
        },
        {
          id: 'reservation',
          name: 'Rezervasyon & Takvim',
          description: 'Rezervasyon Talep Çağrısı (CTA), Canlı Müsaitlik Takvimi',
          sectionTypes: ['RESERVATION_CTA', 'AVAILABILITY_CALENDAR', 'CONTACT'],
        },
        {
          id: 'media',
          name: 'Görseller & Sosyal Kanıt',
          description: 'Fotoğraf Galerisi, İstatistikler ve Müşteri Yorumları',
          sectionTypes: ['GALLERY', 'STATS', 'TESTIMONIALS', 'BLOG_PREVIEW'],
        },
        {
          id: 'advanced',
          name: 'Gelişmiş & Düzen',
          description: 'Özel Zengin İçerik Alanı ve Alt Bilgi (Footer)',
          sectionTypes: ['CUSTOM_CONTENT', 'FOOTER'],
        },
      ];
    }

    // Corporate Categories
    return [
      {
        id: 'header_hero',
        name: 'Giriş & Başlık',
        description: 'Üst Navigasyon Menüsü ve Etkileyici Manşet Alanı',
        sectionTypes: ['HEADER', 'HERO'],
      },
      {
        id: 'corporate_content',
        name: 'Kurumsal & Çözümler',
        description: 'Hizmetler, Hakkımızda, İstatistikler, Ekip ve Referanslar',
        sectionTypes: ['SERVICES', 'ABOUT', 'STATS', 'TEAM', 'REFERENCES'],
      },
      {
        id: 'pricing',
        name: 'Fiyatlandırma & Paketler',
        description: 'Hizmet Paketleri ve Fiyat Tabloları',
        sectionTypes: ['PRICING'],
      },
      {
        id: 'media_trust',
        name: 'Medya & İtibar',
        description: 'Galeri, Müşteri Yorumları, SSS ve Kurumsal Blog Vitrini',
        sectionTypes: ['GALLERY', 'TESTIMONIALS', 'FAQ', 'BLOG_PREVIEW'],
      },
      {
        id: 'contact_footer',
        name: 'İletişim & Alt Bilgi',
        description: 'İletişim Formu, Özel İçerik ve Footer',
        sectionTypes: ['CONTACT', 'CUSTOM_CONTENT', 'FOOTER'],
      },
    ];
  }
}

export const capabilityService = new CapabilityService();
export default capabilityService;
